import React, { useCallback, useEffect, useState } from "react";
import { Building2, Plus, ShieldCheck, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IsolationBadge } from "@/components/app/WorkspaceSwitcher";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_OPTIONS = [
  { value: "six_admin", label: "SIX Admin" },
  { value: "six_consultant", label: "SIX Consultant" },
  { value: "client_user", label: "Client User" },
  { value: "demo_viewer", label: "Demo Viewer (read-only)" },
];

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={15} className="text-[--tx-3]" />
    <h2 className="font-display text-base font-semibold text-[--tx-1]">{children}</h2>
  </div>
);

const UsersTab = ({ workspaces }) => {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "client_user", workspace_ids: [], password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/center/users").then((r) => setUsers(r.data)).catch(() => toast.error("Failed to load users"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    setBusy(true);
    try {
      await api.post("/center/users", form);
      toast.success(`User ${form.email} created`);
      setOpen(false);
      setForm({ email: "", name: "", role: "client_user", workspace_ids: [], password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/center/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`${u.email} ${u.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/center/users/${u.id}`, { role });
      toast.success(`${u.email} role updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <SectionTitle icon={Users}>User accounts</SectionTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-8 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white text-xs font-semibold active:scale-[0.98]" data-testid="admin-invite-user-button">
              <UserPlus size={13} className="mr-1.5" /> Create user
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[--shell-1] border-[--hairline] max-w-md" data-testid="admin-create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-[--tx-1]">Create user account</DialogTitle>
              <DialogDescription className="text-xs text-[--tx-3]">
                Accounts are scoped to workspaces. Demo viewers are read-only.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-[--tx-2]">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="person@company.com" className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9"
                  data-testid="admin-user-email-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[--tx-2]">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe" className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9"
                  data-testid="admin-user-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[--tx-2]">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9 text-xs" data-testid="admin-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[--shell-2] border-[--hairline]">
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value} className="text-xs" data-testid={`admin-role-option-${r.value}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[--tx-2]">Workspace access</Label>
                <div className="space-y-1.5">
                  {workspaces.map((w) => (
                    <label key={w.id} className="flex items-center gap-2 text-xs text-[--tx-2] cursor-pointer">
                      <Checkbox
                        checked={form.workspace_ids.includes(w.id)}
                        onCheckedChange={(c) => setForm({
                          ...form,
                          workspace_ids: c ? [...form.workspace_ids, w.id] : form.workspace_ids.filter((x) => x !== w.id),
                        })}
                        data-testid={`admin-user-workspace-checkbox-${w.id}`}
                      />
                      {w.name} <IsolationBadge badge={w.badge} />
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[--tx-2]">Temporary password (min 8 chars)</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Share securely with the user" className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9"
                  data-testid="admin-user-password-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="h-8 text-xs border-[--hairline] bg-[--shell-2] text-[--tx-2]">Cancel</Button>
              <Button onClick={createUser} disabled={busy || !form.email || !form.password}
                className="h-8 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white text-xs font-semibold"
                data-testid="admin-user-create-submit">
                {busy ? "Creating..." : "Create account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] overflow-hidden">
        <Table data-testid="admin-users-table">
          <TableHeader>
            <TableRow className="border-[--divider] hover:bg-transparent">
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">User</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Role</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Workspaces</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Last login</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users === null && (
              <TableRow><TableCell colSpan={5} className="text-center text-xs text-[--tx-3] py-6">Loading users...</TableCell></TableRow>
            )}
            {users?.map((u) => (
              <TableRow key={u.id} className="border-[--divider] hover:bg-white/[0.03]" data-testid={`admin-user-row-${u.id}`}>
                <TableCell>
                  <div className="text-xs font-semibold text-[--tx-1]">{u.name}</div>
                  <div className="text-[10px] font-mono text-[--tx-3]">{u.email}</div>
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="h-7 w-[150px] text-[11px] bg-[--shell-2] border-[--hairline] text-[--tx-2]" data-testid={`admin-role-select-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[--shell-2] border-[--hairline]">
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-[10px] font-mono text-[--tx-3]">{(u.workspace_ids || []).join(", ") || "-"}</TableCell>
                <TableCell className="text-[10px] font-mono text-[--tx-3]">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "never"}</TableCell>
                <TableCell>
                  <Switch checked={!!u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`admin-user-active-switch-${u.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const WorkspacesTab = ({ workspaces, reload }) => {
  const [wsOpen, setWsOpen] = useState(false);
  const [prjOpen, setPrjOpen] = useState(false);
  const [wsForm, setWsForm] = useState({ name: "", type: "client", description: "" });
  const [prjForm, setPrjForm] = useState({ workspace_id: "", name: "", industry: "", use_case: "", description: "" });
  const [busy, setBusy] = useState(false);

  const createWs = async () => {
    setBusy(true);
    try {
      await api.post("/center/workspaces", wsForm);
      toast.success(`Workspace "${wsForm.name}" created`);
      setWsOpen(false); setWsForm({ name: "", type: "client", description: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const createPrj = async () => {
    setBusy(true);
    try {
      await api.post("/center/projects", prjForm);
      toast.success(`Project "${prjForm.name}" created`);
      setPrjOpen(false); setPrjForm({ workspace_id: "", name: "", industry: "", use_case: "", description: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <SectionTitle icon={Building2}>Workspaces & projects</SectionTitle>
        <div className="flex gap-2">
          <Dialog open={prjOpen} onOpenChange={setPrjOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-8 text-xs border-[--hairline] bg-[--shell-2] text-[--tx-2] hover:text-[--tx-1]" data-testid="admin-create-project-button">
                <Plus size={13} className="mr-1" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[--shell-1] border-[--hairline] max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-[--tx-1]">Create project</DialogTitle>
                <DialogDescription className="text-xs text-[--tx-3]">Every project gets fully isolated data collections.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Workspace</Label>
                  <Select value={prjForm.workspace_id} onValueChange={(v) => setPrjForm({ ...prjForm, workspace_id: v })}>
                    <SelectTrigger className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9 text-xs" data-testid="admin-project-workspace-select">
                      <SelectValue placeholder="Select workspace" />
                    </SelectTrigger>
                    <SelectContent className="bg-[--shell-2] border-[--hairline]">
                      {workspaces.map((w) => (
                        <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Project name</Label>
                  <Input value={prjForm.name} onChange={(e) => setPrjForm({ ...prjForm, name: e.target.value })}
                    className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9" data-testid="admin-project-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Industry</Label>
                  <Input value={prjForm.industry} onChange={(e) => setPrjForm({ ...prjForm, industry: e.target.value })}
                    className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9" data-testid="admin-project-industry-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Use case</Label>
                  <Input value={prjForm.use_case} onChange={(e) => setPrjForm({ ...prjForm, use_case: e.target.value })}
                    className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9" data-testid="admin-project-usecase-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPrjOpen(false)} className="h-8 text-xs border-[--hairline] bg-[--shell-2] text-[--tx-2]">Cancel</Button>
                <Button onClick={createPrj} disabled={busy || !prjForm.name || !prjForm.workspace_id}
                  className="h-8 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white text-xs font-semibold" data-testid="admin-project-create-submit">
                  {busy ? "Creating..." : "Create project"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={wsOpen} onOpenChange={setWsOpen}>
            <DialogTrigger asChild>
              <Button className="h-8 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white text-xs font-semibold active:scale-[0.98]" data-testid="admin-create-workspace-button">
                <Plus size={13} className="mr-1" /> New workspace
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[--shell-1] border-[--hairline] max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-[--tx-1]">Create workspace</DialogTitle>
                <DialogDescription className="text-xs text-[--tx-3]">A workspace is a tenant boundary - files, chat, agents, and logs never cross it.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Name</Label>
                  <Input value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })}
                    className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9" data-testid="admin-workspace-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[--tx-2]">Type</Label>
                  <Select value={wsForm.type} onValueChange={(v) => setWsForm({ ...wsForm, type: v })}>
                    <SelectTrigger className="bg-[--shell-2] border-[--hairline] text-[--tx-1] h-9 text-xs" data-testid="admin-workspace-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[--shell-2] border-[--hairline]">
                      <SelectItem value="client" className="text-xs">Client (CLIENT badge)</SelectItem>
                      <SelectItem value="internal" className="text-xs">Internal (PRIVATE badge)</SelectItem>
                      <SelectItem value="demo" className="text-xs">Demonstration (DEMO badge)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setWsOpen(false)} className="h-8 text-xs border-[--hairline] bg-[--shell-2] text-[--tx-2]">Cancel</Button>
                <Button onClick={createWs} disabled={busy || !wsForm.name}
                  className="h-8 bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white text-xs font-semibold" data-testid="admin-workspace-create-submit">
                  {busy ? "Creating..." : "Create workspace"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {workspaces.map((w) => (
          <Card key={w.id} className="bg-[--panel-dark] border-[--hairline]" data-testid={`admin-workspace-card-${w.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display text-sm font-semibold text-[--tx-1]">{w.name}</span>
                <IsolationBadge badge={w.badge} />
                <span className="ml-auto text-[10px] font-mono text-[--tx-3]">{w.id}</span>
              </div>
              <p className="text-[11px] text-[--tx-3] leading-snug mb-3">{w.description}</p>
              <div className="space-y-1.5">
                {(w.projects || []).map((p) => (
                  <div key={p.id} className="rounded-lg border border-[--divider] bg-[--shell-2] px-2.5 py-2" data-testid={`admin-project-row-${p.id}`}>
                    <div className="text-xs font-semibold text-[--tx-1]">{p.name}</div>
                    <div className="text-[10px] text-[--tx-3]">{p.industry} · {p.use_case}</div>
                  </div>
                ))}
                {(w.projects || []).length === 0 && (
                  <div className="text-[11px] text-[--tx-3] italic">No projects yet.</div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const AuditTab = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/center/audit?limit=200").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load audit log"));
  }, []);
  return (
    <div>
      <SectionTitle icon={ShieldCheck}>Center audit log</SectionTitle>
      <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] overflow-hidden">
        <Table data-testid="admin-audit-table">
          <TableHeader>
            <TableRow className="border-[--divider] hover:bg-transparent">
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Time</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Actor</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Action</TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && (
              <TableRow><TableCell colSpan={4} className="text-center text-xs text-[--tx-3] py-6">Loading audit log...</TableCell></TableRow>
            )}
            {rows?.map((r) => (
              <TableRow key={r.id} className="border-[--divider] hover:bg-white/[0.03]">
                <TableCell className="text-[10px] font-mono text-[--tx-3] whitespace-nowrap">{(r.ts || "").slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="text-[11px] font-mono text-[--tx-2]">{r.actor}</TableCell>
                <TableCell className="text-[11px] font-semibold text-[--tx-1]">{r.action}</TableCell>
                <TableCell className="text-[11px] text-[--tx-3]">{r.detail}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default function AdminConsolePage() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);

  const reload = useCallback(() => {
    api.get("/center/workspaces").then((r) => setWorkspaces(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.role === "six_admin") reload(); }, [user, reload]);

  if (user && user.role !== "six_admin") {
    return (
      <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-8 text-center" data-testid="admin-access-denied">
        <ShieldCheck size={24} className="mx-auto text-[--tx-3] mb-2" />
        <div className="font-display text-sm font-semibold text-[--tx-1]">Admin console requires a SIX admin account</div>
        <p className="text-xs text-[--tx-3] mt-1">Ask your SIX engagement lead to provision access.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-console-page">
      <div className="mb-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[--six-crimson] mb-1">Center administration</div>
        <h1 className="font-display text-2xl font-bold text-[--tx-1] tracking-tight">Tenants, users & access</h1>
        <p className="text-xs text-[--tx-3] mt-1 max-w-xl">
          Workspaces are hard tenant boundaries. Every project inside a workspace has fully isolated
          data collections, uploads, agents, and audit trails.
        </p>
      </div>
      <Tabs defaultValue="users">
        <TabsList className="bg-[--shell-2] border border-[--hairline] mb-4">
          <TabsTrigger value="users" className="text-xs" data-testid="admin-users-tab">Users</TabsTrigger>
          <TabsTrigger value="workspaces" className="text-xs" data-testid="admin-tenants-tab">Workspaces & Projects</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs" data-testid="admin-audit-tab">Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab workspaces={workspaces} /></TabsContent>
        <TabsContent value="workspaces"><WorkspacesTab workspaces={workspaces} reload={reload} /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
